import React from 'react';
import {
  Box, VStack, HStack, Text, Icon, Avatar, Badge,
  useColorModeValue, useDisclosure,
} from '@chakra-ui/react';
import {
  FiHome, FiUsers, FiSettings, FiLayers, FiGrid, FiDollarSign,
  FiPercent, FiUserCheck, FiShield, FiFileText, FiPackage, FiLogOut,
  FiMail, FiSend, FiSmartphone, FiClock, FiKey, FiTrendingUp, FiSliders, FiCalendar,
  FiCreditCard, FiCode,
  FiRepeat, FiLink, FiActivity, FiExternalLink,
} from 'react-icons/fi';
import SuperadminHotelSwitchModal from './SuperadminHotelSwitchModal';
import LanguageMenu from './LanguageMenu';
import { useTranslation } from 'react-i18next';

// Superuser-only screens. The backend gates every underlying endpoint on
// IsSuperUser regardless; hiding the nav just avoids showing a 403 page.
// Restored to admine 2026-10-24 (owner request), superadmin-visible only.
const SUPERUSER_NAV_ITEMS = [
  { divider: true, labelKey: 'sidebar.sections.superadmin' },
  { labelKey: 'sidebar.items.channelManager', icon: FiLink, path: '/channel-manager' },
  { labelKey: 'sidebar.items.userActivity', icon: FiActivity, path: '/user-activity' },
];

// The staff PMS lives on the same domain without the admin. prefix
// (admin.staydesko.com → staydesko.com/dashboard). Owners asked for a way
// back to the calendar from the admin (Villa X, 2026-11-05); the PMS
// already links here from its user menu.
export const frontDeskUrl = (hostname = window.location.hostname) => {
  if (hostname.startsWith('admin.')) return `https://${hostname.slice('admin.'.length)}/dashboard`;
  return 'https://staydesko.com/dashboard';
};

const NAV_ITEMS = [
  { labelKey: 'sidebar.items.overview', icon: FiHome, path: '/' },
  { labelKey: 'sidebar.items.frontDesk', icon: FiExternalLink, href: frontDeskUrl },
  { divider: true, labelKey: 'sidebar.sections.hotelSetup' },
  { labelKey: 'sidebar.items.hotelSettings', icon: FiSettings, path: '/settings' },
  { labelKey: 'sidebar.items.staffManagement', icon: FiUsers, path: '/staff' },
  { labelKey: 'sidebar.items.roomTypes', icon: FiLayers, path: '/room-types' },
  { labelKey: 'sidebar.items.rooms', icon: FiGrid, path: '/rooms' },
  { divider: true, labelKey: 'sidebar.sections.ratesBilling' },
  { labelKey: 'sidebar.items.ratePlans', icon: FiSliders, path: '/rate-plans' },
  { labelKey: 'sidebar.items.taxRates', icon: FiPercent, path: '/tax-rates' },
  { labelKey: 'sidebar.items.chargeableItems', icon: FiDollarSign, path: '/charge-presets' },
  { labelKey: 'sidebar.items.packages', icon: FiPackage, path: '/packages' },
  { labelKey: 'sidebar.items.cancellationPolicies', icon: FiFileText, path: '/cancellation-policies' },
  { divider: true, labelKey: 'sidebar.sections.guestsMarketing' },
  { labelKey: 'sidebar.items.guests', icon: FiUserCheck, path: '/guests' },
  { labelKey: 'sidebar.items.guestApp', icon: FiSmartphone, path: '/guest-app' },
  { labelKey: 'sidebar.items.newsletters', icon: FiSend, path: '/newsletters' },
  { labelKey: 'sidebar.items.emailSmtp', icon: FiMail, path: '/email-settings' },
  { labelKey: 'sidebar.items.bookingEngine', icon: FiCreditCard, path: '/booking-engine-payments' },
  { labelKey: 'sidebar.items.websiteWidget', icon: FiCode, path: '/booking-widget' },
  { labelKey: 'sidebar.items.reminderEmails', icon: FiClock, path: '/reminder-emails' },
  { labelKey: 'sidebar.items.icalSync', icon: FiCalendar, path: '/ical-sync' },
  { labelKey: 'sidebar.items.emailBookings', icon: FiMail, path: '/email-bookings' },
  { labelKey: 'sidebar.items.customValues', icon: FiKey, path: '/custom-values' },
  { labelKey: 'sidebar.items.otaRateModifiers', icon: FiTrendingUp, path: '/rate-modifiers' },
  { divider: true, labelKey: 'sidebar.sections.logs' },
  { labelKey: 'sidebar.items.auditLogs', icon: FiShield, path: '/audit-logs' },
];

// `inDrawer`: rendered inside the phone Drawer (App.js) — fill the drawer
// instead of pinning to the viewport.
const Sidebar = ({ activePath, onNavigate, user, onLogout, inDrawer = false }) => {
  const { t } = useTranslation();
  // Superadmin property switch (owner tool) — modal lives here so both the
  // desktop sidebar and the phone drawer get it for free.
  const hotelSwitch = useDisclosure();
  const bg = useColorModeValue('white', 'gray.800');
  const activeBg = useColorModeValue('blue.50', 'blue.900');
  const activeColor = useColorModeValue('blue.600', 'blue.200');
  const hoverBg = useColorModeValue('gray.100', 'gray.700');

  const hotelName = user?.user_profile?.hotel?.name || t('sidebar.hotelFallback');
  const userName = `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || user?.username;
  const role = user?.user_profile?.role || '';

  return (
    <Box
      w={inDrawer ? '100%' : '260px'}
      h={inDrawer ? '100%' : '100vh'}
      bg={bg}
      borderRight={inDrawer ? 'none' : '1px'}
      borderColor="gray.200"
      position={inDrawer ? 'relative' : 'fixed'}
      left={0}
      top={0}
      display="flex"
      flexDirection="column"
    >
      {/* Hotel header */}
      <Box p={4} borderBottom="1px" borderColor="gray.200">
        <Text fontSize="lg" fontWeight="bold" color="brand.600">{hotelName}</Text>
        <Text fontSize="xs" color="gray.500">{t('common.adminPanel')}</Text>
      </Box>

      {/* Nav items — the scrolling region. The container is a fixed,
          viewport-height flex column, so the hotel header above and the
          user footer below stay pinned while this list scrolls. minH={0}
          is required for a flex child to actually shrink and overflow. */}
      <VStack spacing={0} align="stretch" flex={1} minH={0} overflowY="auto" py={2}>
        {[...NAV_ITEMS, ...(user?.is_superuser ? SUPERUSER_NAV_ITEMS : [])].map((item, i) => {
          if (item.divider) {
            return (
              <Box key={i} px={4} pt={4} pb={1}>
                <Text fontSize="xs" fontWeight="bold" color="gray.400" textTransform="uppercase" letterSpacing="wider">
                  {t(item.labelKey)}
                </Text>
              </Box>
            );
          }

          const isActive = activePath === item.path;
          // External links open in a new tab so the admin stays open behind them.
          const external = typeof item.href === 'function'
            ? { as: 'a', href: item.href(), target: '_blank', rel: 'noopener noreferrer' }
            : {};
          return (
            <HStack
              key={item.path || item.labelKey}
              {...external}
              px={4}
              py={2}
              cursor="pointer"
              bg={isActive ? activeBg : 'transparent'}
              color={isActive ? activeColor : 'gray.700'}
              fontWeight={isActive ? '600' : '400'}
              _hover={{ bg: isActive ? activeBg : hoverBg }}
              onClick={item.href ? undefined : () => onNavigate(item.path)}
              borderLeft="3px solid"
              borderColor={isActive ? 'blue.500' : 'transparent'}
              transition="all 0.15s"
            >
              <Icon as={item.icon} boxSize={4} />
              <Text fontSize="sm">{t(item.labelKey)}</Text>
            </HStack>
          );
        })}
      </VStack>

      {/* User footer */}
      <Box p={4} borderTop="1px" borderColor="gray.200">
        {user?.is_superuser && (
          <>
            <HStack
              cursor="pointer"
              color="blue.600"
              _hover={{ color: 'blue.800' }}
              onClick={hotelSwitch.onOpen}
              spacing={2}
              mb={3}
            >
              <Icon as={FiRepeat} boxSize={4} />
              <Text fontSize="sm" fontWeight="500">{t('sidebar.switchProperty')}</Text>
            </HStack>
            <SuperadminHotelSwitchModal isOpen={hotelSwitch.isOpen} onClose={hotelSwitch.onClose} />
          </>
        )}
        <LanguageMenu />
        <HStack spacing={3} mb={3}>
          <Avatar size="sm" name={userName} />
          <Box flex={1} minW={0}>
            <Text fontSize="sm" fontWeight="500" isTruncated>{userName}</Text>
            <Badge colorScheme="blue" fontSize="2xs">{role}</Badge>
          </Box>
        </HStack>
        <HStack
          cursor="pointer"
          color="gray.500"
          _hover={{ color: 'red.500' }}
          onClick={onLogout}
          spacing={2}
        >
          <Icon as={FiLogOut} boxSize={4} />
          <Text fontSize="sm">{t('sidebar.signOut')}</Text>
        </HStack>
      </Box>
    </Box>
  );
};

export default Sidebar;
